import React, { useState } from "react";

import { useFetchLabelDismissalsQuery } from "../../../api/printingApi";
import { useLanguage } from "../../../contexts/LanguageContext";
import { Btn, EmptyState, Pill, Spinner } from "../../catalogProductStudio/edit/StudioUi";
import { fill, formatDateTime, plural } from "../printCenterModel";

// Variant titles are stored as "Product / Colour / Size"; show only the variant part.
const variantPart = ({ productTitle, variantTitle }) => {
  const title = String(variantTitle || "");
  const prefix = `${productTitle} / `;
  return title.startsWith(prefix) ? title.slice(prefix.length) : title;
};

/**
 * Audit trail of labels marked as not needed. Fully dismissed demand leaves
 * the Needs labels queue; it stays visible here (quantity, reason, who, when).
 */
function DismissalHistoryView({ tt }) {
  const { language } = useLanguage();
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, isError, refetch } = useFetchLabelDismissalsQuery({ page, pageSize: 20 });

  if (isLoading) return <div className="pc-loading"><Spinner label={tt("loading")} /></div>;
  if (isError || !data) {
    return <EmptyState icon="alert" title={tt("loadError")} action={<Btn onClick={refetch}>{tt("retry")}</Btn>} />;
  }
  if (!data.items.length) {
    return <EmptyState icon="history" title={tt("dismissalsEmptyTitle")}>{tt("dismissalsEmptyCopy")}</EmptyState>;
  }

  return (
    <>
      {isFetching ? <Spinner /> : null}
      <ul className="pc-dismissals">
        {data.items.map((entry) => (
          <li key={entry.id} className="pc-dismissal">
            <div className="pc-dismissal__main">
              <strong>
                {entry.item.productTitle}
                {variantPart(entry.item) ? ` · ${variantPart(entry.item)}` : ""}
              </strong>
              <code className="pc-small">{entry.item.barcode || "—"}</code>
              <span className="pc-muted pc-small">
                {fill(tt(`receiptName_${entry.receipt.kind}`), { id: entry.receipt.id })} ·{" "}
                {formatDateTime(entry.receipt.receivedAt, language)}
              </span>
            </div>
            <div className="pc-dismissal__audit">
              <strong>{plural(tt, "dismissedCount", entry.quantity)}</strong>
              <span className="pc-dismissal__reason">“{entry.reason}”</span>
              <span className="pc-muted pc-small">
                {entry.actorName || "—"} · {formatDateTime(entry.createdAt, language)}
              </span>
            </div>
            {entry.demand.closed ? <Pill tone="neutral">{tt("dismissalClosed")}</Pill> : null}
          </li>
        ))}
      </ul>
      {data.totalPages > 1 ? (
        <div className="pc-pager">
          <Btn size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>{tt("previous")}</Btn>
          <span className="pc-muted pc-small">{fill(tt("pageOf"), { page, total: data.totalPages })}</span>
          <Btn size="sm" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>{tt("next")}</Btn>
        </div>
      ) : null}
    </>
  );
}

export default DismissalHistoryView;
